import React from 'react';
import { uidNoDash, UnBoxArrayType } from '~/utils';

import { SchemaHolder } from './schema';
import { useSearchHistory } from './useSearchHistory';
import { useFilterSummary } from './useFilterSummary';
import { QueryParameters, QueryParametersKey, QueryParametersValue, ChangeSource, FilterSummaryItem } from './types';

type QueryParameterState<Parameters extends QueryParameters> = {
  rnd: string;
  queryId: string;
  parameters: Parameters;
};

export type QueryParameterContextState<Parameters extends QueryParameters> = QueryParameterState<Parameters> & {
  resetQueryParameters: () => void;
  filterSummaries: FilterSummaryItem<Parameters, keyof Parameters>[];
  setParameters: (source: ChangeSource, changes: Partial<Parameters>) => void;
  removeQueryParameterValue: <Key extends QueryParametersKey<Parameters>>(
    key: Key,
    removedValue: UnBoxArrayType<QueryParametersValue<Parameters, Key>>
  ) => void;
};
export function QueryParameterContextProvider<StaticProps, SearchQuery, Parameters extends QueryParameters>(props: {
  children: React.ReactNode;
  staticProps: StaticProps;
  defaultParameters: Parameters;
  context: Context<Parameters>;
  schemaHolder: SchemaHolder<StaticProps, SearchQuery, Parameters>;
}) {
  const [{ parameters, rnd, queryId }, setSearchQueryParameter] = React.useState<QueryParameterState<Parameters>>(
    () => ({
      rnd: uidNoDash(),
      queryId: uidNoDash(),
      parameters: props.defaultParameters,
    })
  );
  const { parse, push, urlRnd } = useSearchHistory<StaticProps, SearchQuery, Parameters>(
    props.staticProps,
    props.defaultParameters,
    props.schemaHolder
  );

  const { filterSummaries } = useFilterSummary({
    defaultParameters: props.defaultParameters,
    parameters: parameters,
    schema: props.schemaHolder,
    staticProps: props.staticProps,
  });

  const setParameters = React.useCallback(
    (source: ChangeSource, changes: Partial<Parameters>) => {
      const rnd = uidNoDash();
      const sanitizedParameters = sanitizeParams(props.schemaHolder, props.staticProps, props.defaultParameters, {
        ...parameters,
        ...changes,
      });

      const qid = uidNoDash();
      const updatedParameters = { rnd, parameters: sanitizedParameters, queryId: qid };
      setSearchQueryParameter(updatedParameters);
      push(rnd, sanitizedParameters);
    },
    [props.schemaHolder, props.staticProps, props.defaultParameters, parameters, push]
  );

  const resetQueryParameters = React.useCallback(() => {
    setParameters('FilterSummary', {
      ...props.defaultParameters,
      pageSize: parameters.pageSize, // we should keep the selected pageSize
    });
  }, [setParameters, parameters, props.defaultParameters]);

  const removeQueryParameterValue = React.useCallback(
    <Key extends QueryParametersKey<Parameters>>(
      key: Key,
      removedValue: UnBoxArrayType<QueryParametersValue<Parameters, Key>>
    ) => {
      const filterSchema = props.schemaHolder.get(key);
      if (!filterSchema.removeParam) return;

      const value = filterSchema.getValue({ parameters, staticProps: props.staticProps });
      const defaultValue = filterSchema.getDefaultValue({ defaultParameters: props.defaultParameters });
      const result = filterSchema.removeParam({
        parameters,
        defaultValue,
        value,
        removedValue,
        staticProps: props.staticProps,
      }) as Partial<Parameters> | undefined;
      if (!result) return;

      setParameters('FilterSummary', { ...parameters, ...result, pageNumber: 1 });
    },
    [setParameters, parameters, props.staticProps, props.defaultParameters, props.schemaHolder]
  );

  const value = React.useMemo(
    () => ({
      rnd,
      queryId,
      parameters,
      setParameters,
      filterSummaries,
      resetQueryParameters,
      removeQueryParameterValue,
      staticProps: props.staticProps,
      defaultParameters: props.defaultParameters,
    }),
    [
      filterSummaries,
      parameters,
      rnd,
      queryId,
      setParameters,
      resetQueryParameters,
      removeQueryParameterValue,
      props.staticProps,
      props.defaultParameters,
    ]
  );

  // whenever URL changes
  React.useEffect(() => {
    if (urlRnd === rnd) return;

    const sanitizedUrlQueryParameters = sanitizeParams(
      props.schemaHolder,
      props.staticProps,
      props.defaultParameters,
      parse()
    );
    setSearchQueryParameter({
      queryId: uidNoDash(),
      rnd: urlRnd || rnd,
      parameters: sanitizedUrlQueryParameters,
    });
  }, [urlRnd, rnd, parse, props.schemaHolder, props.staticProps, props.defaultParameters]);

  return <props.context.Provider value={value}> {props.children} </props.context.Provider>;
}

function sanitizeParams<StaticProps, SearchQuery, Parameters extends QueryParameters>(
  schemaHolder: SchemaHolder<StaticProps, SearchQuery, Parameters>,
  staticProps: StaticProps,
  defaultParameters: Parameters,
  parameters: Parameters
) {
  const result = { ...parameters };
  schemaHolder.parameters.forEach((parameter) => {
    if (!parameter.sanitize) return;
    const value = parameter.getValue({ staticProps, parameters });
    const defaultValue = parameter.getDefaultValue({ defaultParameters });
    const params = parameter.sanitize({ value, staticProps, defaultValue, parameters });
    Object.assign(result, params);
  });

  return result;
}

export type Context<Parameters extends QueryParameters> = React.Context<
  QueryParameterContextState<Parameters> | undefined
>;

export function useContext<Parameters extends QueryParameters>(context: Context<Parameters>) {
  const ctx = React.useContext(context);
  if (!ctx) {
    throw new Error('useContext must be used within a QueryParameterContextProvider');
  }
  return ctx;
}
